import type { Patient } from '@medplum/fhirtypes';
import { useEffect, useState } from 'react';

import { formatFhirDate, formatHumanName } from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';
import { ELUVE_FHIR_NAMESPACE } from '@eluve/plan-shapes';

// Mirrors the backend's `buildIdentifierSystem('patient')` — the system the
// sync stamps on every FHIR Patient (this lib can't import nestjs-medplum).
const PATIENT_ID_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/patient-id`;

export interface PatientSummary {
  readonly display: string;
  readonly detail: string | null;
}

const patientDisplay = (p: Patient): string | null =>
  formatHumanName(p.name?.[0]) || null;

const patientMrn = (p: Patient): string | null =>
  p.identifier?.find((i) =>
    i.type?.coding?.some((coding) => coding.code === 'MR'),
  )?.value ?? null;

const patientGenderText = (p: Patient): string | null =>
  p.gender ? p.gender.charAt(0).toUpperCase() + p.gender.slice(1) : null;

/**
 * Best-effort browser-direct read of the session's Patient for the
 * create-task sheet's read-only patient row: name plus an MRN · DOB · gender
 * line, silently dropping whatever's missing. Null while loading, on any
 * fetch error, or when the Medplum client / patient id isn't available.
 */
export function usePatientSummary(
  patientId: string | undefined,
): PatientSummary | null {
  const { client } = useMedplum();
  const [summary, setSummary] = useState<PatientSummary | null>(null);

  useEffect(() => {
    setSummary(null);
    if (!client || !patientId) return;
    let cancelled = false;
    void (async () => {
      try {
        const patient = await client.searchOne('Patient', {
          identifier: `${PATIENT_ID_SYSTEM}|${patientId}`,
        });
        if (cancelled || !patient) return;
        const display = patientDisplay(patient);
        if (!display) return;
        const mrn = patientMrn(patient);
        const dob = formatFhirDate(patient.birthDate);
        const parts = [
          mrn ? `MRN ${mrn}` : null,
          dob ? `DOB ${dob}` : null,
          patientGenderText(patient),
        ].filter(Boolean);
        setSummary({
          display,
          detail: parts.length > 0 ? parts.join(' · ') : null,
        });
      } catch {
        // Best-effort: the sheet degrades to a nameless row.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, patientId]);

  return summary;
}
